import MatterCard from './matterCard';
import { useFetchPrivateMatter } from '../../hooks/useFetchPrivateMatter';
import { useFetchCompanyMatter } from '../../hooks/useFetchCompanyMatter';
import { useInView } from 'react-intersection-observer';
import { useEffect, useMemo, useState } from 'react';
import { Spin } from '../Ant';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ICustomerType } from '../../constants/customer';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { IMatter } from '../../interface/matter';
import NoData from '../dashboard/noData';
import { BriefcaseEmpty } from '../icons';
import { ISortOption, IActiveSortState } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const MattersListing = ({ type }: { type: string }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id: customerId } = useParams();
  const isCompany = type === ICustomerType.COMPANY;
  const [searchValue, setSearchValue] = useState<string>('');
  const matterSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'openingDate',
        label: t('matter:openingDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
      { field: 'subject', label: t('matter:subject'), type: 'text', defaultDirection: 'ASC' },
      {
        field: 'matterNumber',
        label: t('matter:matterNumber'),
        type: 'number',
        defaultDirection: 'ASC',
      },
      {
        field: 'principal.name',
        label: t('matter:principal'),
        type: 'text',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );

  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'openingDate',
    direction: 'DESC',
  });

  const defaultParams = {
    limit: 10,
    customerId: customerId ?? '',
    search: searchValue,
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    withHasSubMatters: true,
    relations: [
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'responsibleUser:fullName',
      'parties',
      'parties.roles',
      'parties.roles.matterPartyRole',
    ],
    fields: [
      'subject',
      'matterNumber',
      'status',
      'invoiceableType',
      'openingDate',
      'hasNotes',
      'hasImportantNotes',
      'hasNotes',
      'hasImportantRelatedNotes',
      'fixedPrice',
      'fixedPriceInUse',
      'parentMatterId',
      'insider',
      'language',
    ],
  };
  const companyMatterResult = useFetchCompanyMatter(defaultParams);
  const privateMatterResult = useFetchPrivateMatter(defaultParams);
  const {
    data: matterList,
    isLoading: isMatterLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = isCompany ? companyMatterResult : privateMatterResult;
  const { ref, inView } = useInView({
    threshold: 0.5,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && matterList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="conflict-matters-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('matter:search')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          handleRefresh={() => {
            queryClient.removeQueries({
              queryKey: [isCompany ? 'company-matter' : 'private-matter'],
            });
            return refetch();
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={matterSortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {matterList.map((val: IMatter) => {
        return (
          <MatterCard
            key={val.id}
            matter={val}
            type={isCompany ? ICustomerType.COMPANY : ICustomerType.PRIVATE_PERSON}
          />
        );
      })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!matterList || (Object.entries(matterList).length === 0 && !isMatterLoading)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={t('matter:noMatters')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<BriefcaseEmpty />}
          />
        </div>
      )}
      {isMatterLoading ? (
        <SkeletonCardList rows={[1, 1, 1, 2]} withSubcontentOnFirstRow />
      ) : (
        renderLoader()
      )}
    </CollapsibleListLayout>
  );
};

export default MattersListing;
